import { ref, reactive } from 'vue'
import type { ContactFormErrors, ContactFormState } from '../types/contact'
import { emailService } from '../services/emailjs'

export function useContactForm() {
  const state = reactive<ContactFormState>({
    form: {
      name: '',
      email: '',
      subject: '',
      message: ''
    },
    errors: {},
    isSubmitting: false,
    isSubmitted: false,
    submitError: null
  })

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateForm = (): boolean => {
    const errors: ContactFormErrors = {}

    // Name validation
    if (!state.form.name.trim()) {
      errors.name = 'Name is required'
    } else if (state.form.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters'
    }

    // Email validation
    if (!state.form.email.trim()) {
      errors.email = 'Email is required'
    } else if (!validateEmail(state.form.email)) {
      errors.email = 'Please enter a valid email address'
    }

    // Subject validation
    if (!state.form.subject.trim()) {
      errors.subject = 'Subject is required'
    } else if (state.form.subject.trim().length < 5) {
      errors.subject = 'Subject must be at least 5 characters'
    }

    // Message validation
    if (!state.form.message.trim()) {
      errors.message = 'Message is required'
    } else if (state.form.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters'
    } else if (state.form.message.trim().length > 1000) {
      errors.message = 'Message must be less than 1000 characters'
    }

    state.errors = errors
    return Object.keys(errors).length === 0
  }

  const clearErrors = (field?: keyof ContactFormErrors) => {
    if (field) {
      delete state.errors[field]
    } else {
      state.errors = {}
    }
  }

  const submitForm = async (): Promise<boolean> => {
    // Reset submission state
    state.submitError = null
    
    // Validate form
    if (!validateForm()) {
      return false
    }

    state.isSubmitting = true

    try {
      // Prepare email template parameters
      const templateParams = {
        from_name: state.form.name,
        from_email: state.form.email,
        subject: state.form.subject,
        message: state.form.message,
        to_email: 'abraham.michael.dev@gmail.com'
      }

      // Send email
      const result = await emailService.sendEmail(templateParams)

      if (result.success) {
        state.isSubmitted = true
        // Reset form after successful submission
        resetForm()
        return true
      } else {
        state.submitError = result.message
        return false
      }
    } catch (error) {
      console.error('Form submission error:', error)
      state.submitError = 'An unexpected error occurred. Please try again.'
      return false
    } finally {
      state.isSubmitting = false
    }
  }

  const resetForm = () => {
    state.form = {
      name: '',
      email: '',
      subject: '',
      message: ''
    }
    state.errors = {}
    state.submitError = null
  }

  const resetSubmissionState = () => {
    state.isSubmitted = false
    state.submitError = null
  }

  // Character count for message field
  const messageCharacterCount = ref(0)
  const updateMessageCount = (value: string) => {
    messageCharacterCount.value = value.length
  }

  return {
    state,
    validateForm,
    clearErrors,
    submitForm,
    resetForm,
    resetSubmissionState,
    messageCharacterCount,
    updateMessageCount
  }
}
